import React, { useEffect, useRef } from 'react';
import './ParticleBackground.css';

const ParticleBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let particles = [];

    // Cache theme colors — only recomputed on theme change
    let cachedColors = null;

    const hexToHsl = (hex) => {
      const r = parseInt(hex.slice(1, 3), 16) / 255;
      const g = parseInt(hex.slice(3, 5), 16) / 255;
      const b = parseInt(hex.slice(5, 7), 16) / 255;

      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      let h, s, l = (max + min) / 2;

      if (max === min) {
        h = s = 0;
      } else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
          case r: h = (g - b) / d + (g < b ? 6 : 0); break;
          case g: h = (b - r) / d + 2; break;
          case b: h = (r - g) / d + 4; break;
          default: h = 0; break;
        }
        h /= 6;
      }

      return [h * 360, s * 100, l * 100];
    };

    const computeThemeColors = () => {
      const root = document.documentElement;
      const computedStyle = getComputedStyle(root);
      const primaryColor = computedStyle.getPropertyValue('--primary-500').trim();
      const isLight = root.getAttribute('data-theme') === 'light';
      const [h, s] = hexToHsl(primaryColor);
      cachedColors = {
        h, s, isLight,
        connectionColor: `hsl(${h}, ${s}%, ${isLight ? 30 : 50}%)`,
      };
    };

    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    const CONNECTION_DIST = 100;
    const CELL_SIZE = CONNECTION_DIST;

    // Particle class
    class Particle {
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.vx = (Math.random() - 0.5) * 0.5;
        this.vy = (Math.random() - 0.5) * 0.5;
        this.size = Math.random() * 2 + 1;
        this.opacity = Math.random() * 0.5 + 0.2;
        this.hue = cachedColors.h + (Math.random() - 0.5) * 60;
        this.saturation = cachedColors.s;
        this.lightness = cachedColors.isLight ? 40 : 60;
        this.color = `hsl(${this.hue}, ${this.saturation}%, ${this.lightness}%)`;
      }

      update() {
        this.x += this.vx;
        this.y += this.vy;

        if (this.x < 0) this.x = canvas.width;
        if (this.x > canvas.width) this.x = 0;
        if (this.y < 0) this.y = canvas.height;
        if (this.y > canvas.height) this.y = 0;
      }

      draw() {
        ctx.globalAlpha = this.opacity;
        ctx.fillStyle = this.color;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // Initialize particles (capped at 80)
    const initParticles = () => {
      particles = [];
      const particleCount = Math.min(80, Math.floor((canvas.width * canvas.height) / 15000));
      for (let i = 0; i < particleCount; i++) {
        particles.push(new Particle());
      }
    };

    // Grid-based spatial lookup for connections — O(n) instead of O(n²)
    const drawConnections = () => {
      const cols = Math.ceil(canvas.width / CELL_SIZE) + 1;
      const rows = Math.ceil(canvas.height / CELL_SIZE) + 1;
      const grid = new Array(cols * rows);

      // Assign particles to grid cells
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        const cx = Math.floor(p.x / CELL_SIZE);
        const cy = Math.floor(p.y / CELL_SIZE);
        const key = cy * cols + cx;
        if (!grid[key]) grid[key] = [];
        grid[key].push(i);
      }

      ctx.strokeStyle = cachedColors.connectionColor;
      ctx.lineWidth = 1;

      // Only check neighboring cells
      for (let cy = 0; cy < rows; cy++) {
        for (let cx = 0; cx < cols; cx++) {
          const cell = grid[cy * cols + cx];
          if (!cell) continue;

          // Check current cell and 3 forward neighbors (right, below, below-right, below-left)
          const neighbors = [
            cy * cols + cx,
            cx + 1 < cols ? cy * cols + cx + 1 : -1,
            cy + 1 < rows ? (cy + 1) * cols + cx : -1,
            cx + 1 < cols && cy + 1 < rows ? (cy + 1) * cols + cx + 1 : -1,
            cx - 1 >= 0 && cy + 1 < rows ? (cy + 1) * cols + cx - 1 : -1,
          ];

          for (const nk of neighbors) {
            if (nk < 0) continue;
            const neighbor = grid[nk];
            if (!neighbor) continue;

            const isSelf = nk === cy * cols + cx;
            for (let ai = 0; ai < cell.length; ai++) {
              const a = particles[cell[ai]];
              const startJ = isSelf ? ai + 1 : 0;
              for (let bi = startJ; bi < neighbor.length; bi++) {
                const b = particles[neighbor[bi]];
                const dx = a.x - b.x;
                const dy = a.y - b.y;
                const distSq = dx * dx + dy * dy;

                if (distSq < CONNECTION_DIST * CONNECTION_DIST) {
                  const dist = Math.sqrt(distSq);
                  ctx.globalAlpha = (CONNECTION_DIST - dist) / CONNECTION_DIST * 0.1;
                  ctx.beginPath();
                  ctx.moveTo(a.x, a.y);
                  ctx.lineTo(b.x, b.y);
                  ctx.stroke();
                }
              }
            }
          }
        }
      }
    };

    // Animation loop
    let frameCount = 0;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      ctx.globalAlpha = 1;
      particles.forEach(particle => {
        particle.update();
        particle.draw();
      });

      // Draw connections every other frame
      if (frameCount % 2 === 0) {
        drawConnections();
      }
      frameCount++;

      animationFrameId = requestAnimationFrame(animate);
    };

    // Initialize
    computeThemeColors();
    resizeCanvas();
    initParticles();
    animate();

    // Handle resize
    const handleResize = () => {
      resizeCanvas();
      initParticles();
    };

    // Handle theme change — recompute cached colors and regenerate particles
    const handleThemeChange = () => {
      computeThemeColors();
      initParticles();
    };

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-theme') {
          handleThemeChange();
        }
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      observer.disconnect();
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="particle-background"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: -1,
        pointerEvents: 'none'
      }}
    />
  );
};

export default ParticleBackground;
